import React, { useEffect, useState } from "react";
import { FaCog, FaTrashAlt } from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import {
  useUpdateFirmSyncMutation,
  useDeleteFirmProductMutation,
} from "../../../store/storageRoles/firmApi";
import { useGetTrustedDevicesQuery } from "../../../store";
import Drawer from "../../shared/Drawer";
import ProductEditForm from "./ProductEditForm";
import PasswordConfirmModal from "../../Utils/PasswordConfirmModal";

/**
 * Ürün ayarları paneli (#316) — merkez firma detayında "Ürün Ayarları"
 * sekmesinin yerine: Cihazlar sayfasındaki ürün kartına tıklanınca sağdan
 * açılır. Sekmedeki her şey burada: kota özeti, ayar alanları (izinlere göre
 * düzenlenebilir), alt firmalara uygula, Kaydet; Ürünü Sil yalnız sistem
 * yöneticisi (şifre + 2FA onayı, sekmedekiyle aynı).
 *
 * Veri çağırandan gelir (getFirmSync — Cihazlar zaten çekiyor):
 * @param {Object|null} product  productInfoes[] öğesi (null → kapalı)
 * @param {Array} quotaArray     cihazlardaki ürün kotaları [{name, quota}]
 * @param {Function} onSaved     kayıt / silme sonrası (veriyi yenile)
 * @param {Function} onNotify    ({severity, message}) — sayfanın uyarı alanı
 */
export default function ProductSettingsDrawer({
  open,
  firm,
  product,
  quotaArray = [],
  readOnly = false,
  onClose,
  onSaved,
  onNotify,
}) {
  const { auth } = useAuth();
  const { isAdmin } = usePermission();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: trustedDevicesData } = useGetTrustedDevicesQuery(undefined, {
    skip: !auth?.accessToken || !isAdmin || !open,
  });
  const twoFactorEnabled = trustedDevicesData?.data?.twoFactorEnabled === true;

  const [updateFirmSync, updateResult] = useUpdateFirmSyncMutation();
  const [deleteFirmProduct, deleteResult] = useDeleteFirmProductMutation();

  useEffect(() => {
    if (updateResult.isSuccess) {
      // Alt firmalara uygulandıysa sonuç sayıları da söylenir (sekmedekiyle aynı)
      const sub = updateResult.data?.data?.subFirms;
      let message = "Ürün ayarları başarıyla güncellendi!";
      if (sub) {
        const parts = [`${sub.applied?.length || 0} alt firmaya uygulandı`];
        if (sub.skipped?.length) parts.push(`${sub.skipped.length} atlandı`);
        if (sub.failed?.length) parts.push(`${sub.failed.length} başarısız`);
        message += ` (${parts.join(", ")})`;
      }
      onNotify?.({ severity: sub?.failed?.length ? "warning" : "success", message });
      updateResult.reset();
      onSaved?.();
      onClose?.();
    }
    if (updateResult.isError) {
      onNotify?.({
        severity: "error",
        message: updateResult.error?.data?.message || "Güncelleme sırasında hata oluştu",
      });
      updateResult.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResult.isSuccess, updateResult.isError]);

  useEffect(() => {
    if (deleteResult.isSuccess) {
      onNotify?.({ severity: "success", message: "Ürün firmadan silindi." });
      setDeleteOpen(false);
      deleteResult.reset();
      onSaved?.();
      onClose?.();
    }
    if (deleteResult.isError) {
      onNotify?.({
        severity: "error",
        message: deleteResult.error?.data?.message || "Ürün silinirken hata oluştu.",
      });
      deleteResult.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteResult.isSuccess, deleteResult.isError]);

  const handleSave = (formData) => {
    updateFirmSync({
      ...formData,
      applyToSubFirms: !!formData.applyToSubFirms,
      productPrice: parseFloat(formData.productPrice),
      quotaMax: parseInt(formData.quotaMax, 10),
      quotaWarning: parseInt(formData.quotaWarning, 10),
      syncLevel: parseInt(formData.syncLevel, 10),
      quotaCoefficient: parseInt(formData.quotaCoefficient, 10),
      orderFrequencyDays: parseInt(formData.orderFrequencyDays, 10),
      goalConsumentMonthly: parseInt(formData.goalConsumentMonthly, 10),
      id: firm._id,
      token: auth.accessToken,
    });
  };

  const handleDeleteConfirm = ({ password, passwordConfirm, code }) => {
    if (!product) return;
    deleteFirmProduct({
      id: firm._id,
      productName: product.productName,
      password,
      passwordConfirm,
      ...(twoFactorEnabled && code ? { code } : {}),
      token: auth.accessToken,
    });
  };

  const isSaving = updateResult.isLoading;
  // Kart hesabıyla aynı: toplam = havuz + cihazlardaki
  const poolQuota = Number(product?.quota) || 0;
  const deviceQuota = Number(quotaArray.find((q) => q.name === product?.productName)?.quota) || 0;
  const closed = product?.quotaDistribution === false;

  const summary = product ? (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
          <span className="text-[11px] font-semibold text-blue-500 uppercase tracking-wide block mb-0.5">
            Toplam Kota
          </span>
          <span className="text-base font-bold text-blue-700 tabular-nums">
            {(poolQuota + deviceQuota).toLocaleString("tr-TR")}
          </span>
        </div>
        <div className="bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
          <span className="text-[11px] font-semibold text-amber-500 uppercase tracking-wide block mb-0.5">
            Dağıtılmamış
          </span>
          <span className="text-base font-bold text-amber-700 tabular-nums">
            {poolQuota.toLocaleString("tr-TR")}
          </span>
        </div>
      </div>
      {closed && (
        <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Bu ürün kota almaya <span className="font-semibold">kapalı</span>; ürün kartlarında görünmez.
          Açmak için aşağıdaki anahtarı kullanın.
        </p>
      )}
    </div>
  ) : null;

  return (
    <>
      <Drawer
        open={open && !!product}
        onClose={() => {
          if (!isSaving) onClose?.();
        }}
        title={product?.productName}
        subtitle="Ürün Ayarları"
        titleIcon={<FaCog className="w-4 h-4 text-purple-600" />}
        width="sm"
      >
        {product && (
          <ProductEditForm
            // Ürün değişince form baştan kurulsun (kendi state'i var)
            key={product.productName}
            variant="drawer"
            product={product}
            onSave={handleSave}
            readOnly={readOnly}
            onClose={() => onClose?.()}
            isLoading={isSaving}
            topSlot={summary}
            footerStart={
              !readOnly && isAdmin ? (
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  title="Ürünü Sil (sadece sistem yöneticisi)"
                >
                  <FaTrashAlt className="w-3 h-3" />
                  Ürünü Sil
                </button>
              ) : null
            }
          />
        )}
      </Drawer>

      <PasswordConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Ürün Silme İşlemi"
        subtitle={`"${product?.productName}" ürünü firmanın ürün listesinden tamamen silinecek. Bu işlem geri alınamaz; ürün aynı adla bir cihazda hâlâ tanımlıysa senkronda firmaya yeniden eklenebilir. Lütfen şifrenizi girerek onaylayın.${twoFactorEnabled ? " İki adımlı doğrulama kodu da gereklidir." : ""}`}
        confirmButtonText="Ürünü Sil"
        isLoading={deleteResult.isLoading}
        twoFactorEnabled={twoFactorEnabled}
      />
    </>
  );
}
